import { useRef, useState } from "react";
import { Popover } from "@base-ui/react/popover";

interface DateTimePickerProps {
    value: string;
    onChange: (value: string) => void;
    ariaLabel: string;
}

const DAY_HOURS = Array.from({ length: 12 }, (_, index) => index + 6);
const NIGHT_HOURS = [...Array.from({ length: 6 }, (_, index) => index + 18), ...Array.from({ length: 6 }, (_, index) => index)];
const MINUTES = [15, 30, 45] as const;

function splitValue(value: string): { date: string; time: string } {
    const [date = "", time = ""] = value.split("T");
    return { date, time: time.slice(0, 5) };
}

function formatHour(hour: number): string {
    const period = hour < 12 ? "AM" : "PM";
    const twelveHour = hour % 12 || 12;
    return `${twelveHour} ${period}`;
}

function formatTime(time: string): string {
    const [hourText, minuteText] = time.split(":");
    const hour = Number(hourText);
    const minute = Number(minuteText);
    if (!Number.isInteger(hour) || !Number.isInteger(minute)) return "Choose time";
    return `${formatHour(hour).replace(" ", `:${String(minute).padStart(2, "0")} `)}`;
}

function combineValue(date: string, time: string): string {
    return date && time ? `${date}T${time}` : "";
}

export default function DateTimePicker({ value, onChange, ariaLabel }: DateTimePickerProps) {
    const { date, time } = splitValue(value);
    const [open, setOpen] = useState(false);
    const [hoveredHour, setHoveredHour] = useState<number | null>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const selectedHour = time ? Number(time.split(":")[0]) : null;
    const selectedMinute = time ? Number(time.split(":")[1]) : null;

    function setDate(nextDate: string) {
        onChange(combineValue(nextDate, time || "09:00"));
    }

    function chooseTime(hour: number, minute: number) {
        const nextTime = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
        const nextDate = date || new Date().toISOString().slice(0, 10);
        onChange(combineValue(nextDate, nextTime));
        setOpen(false);
    }

    function renderHour(hour: number) {
        const expanded = hoveredHour === hour || selectedHour === hour;
        const selected = selectedHour === hour;
        return (
            <div
                key={hour}
                className={`time-picker-hour${selected ? " selected" : ""}`}
                onPointerEnter={() => setHoveredHour(hour)}
                onPointerLeave={() => setHoveredHour(null)}
            >
                <button
                    type="button"
                    className="time-picker-hour-main"
                    onClick={() => chooseTime(hour, 0)}
                    aria-label={`${ariaLabel}, ${formatHour(hour)}`}
                >
                    {formatHour(hour)}
                </button>
                <div className={`time-picker-minutes${expanded ? " visible" : ""}`}>
                    {MINUTES.map(minute => (
                        <button
                            type="button"
                            key={minute}
                            className={selected && selectedMinute === minute ? "selected" : ""}
                            onClick={() => chooseTime(hour, minute)}
                            aria-label={`${ariaLabel}, ${formatHour(hour).replace(" ", `:${minute} `)}`}
                        >
                            :{minute}
                        </button>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <Popover.Root open={open} onOpenChange={setOpen}>
            <div className="date-time-picker">
                <input
                    type="date"
                    value={date}
                    onChange={event => setDate(event.target.value)}
                    aria-label={`${ariaLabel} date`}
                />
                <Popover.Trigger
                    ref={triggerRef}
                    type="button"
                    className="time-picker-trigger"
                    onKeyDown={event => {
                        if (event.key === "ArrowDown") {
                            event.preventDefault();
                            setOpen(true);
                        }
                    }}
                    aria-label={`${ariaLabel} time, ${formatTime(time)}`}
                >
                    <span>{formatTime(time)}</span>
                    <svg aria-hidden="true" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="8" />
                        <path d="M12 7v5l3 2" />
                    </svg>
                </Popover.Trigger>

                <Popover.Portal>
                    <Popover.Positioner
                        className="time-picker-positioner"
                        positionMethod="fixed"
                        sideOffset={6}
                        align="end"
                        collisionPadding={8}
                    >
                        <Popover.Popup
                            className="time-picker-menu"
                            finalFocus={triggerRef}
                            aria-label={`${ariaLabel} time choices`}
                        >
                            <section>
                                <h4>Day</h4>
                                {DAY_HOURS.map(renderHour)}
                            </section>
                            <section>
                                <h4>Night</h4>
                                {NIGHT_HOURS.map(renderHour)}
                            </section>
                        </Popover.Popup>
                    </Popover.Positioner>
                </Popover.Portal>
            </div>
        </Popover.Root>
    );
}
